/**
 * @workspace/feature-flags-shared
 *
 * Single source of truth for feature-flag KEY constants. Both the web app
 * and the mobile app import from here so a typo on one surface can't
 * silently diverge from the other (e.g. one app reads
 * `feature_us_redirect_car_detail` while the other reads
 * `feature_us_redirect_cardetail` and gets `false` forever).
 *
 * The api-server's `feature_flags` table is the runtime source of truth
 * for the *values*. This module only declares the legal *keys*.
 *
 * Adding a flag: append it to FEATURE_FLAG_KEYS, add it to the
 * `FeatureFlagKey` union via the `as const` assertion, and add a row to
 * the api-server `feature_flags` table.
 */

export const HOST_CONFIRMATION_CALLS_FLAG = "feature_host_confirmation_calls" as const;
export const DRIVESHARE_PROMISE_FLAG = "feature_driveshare_promise" as const;
/** Brand-neutral alias for consumers whose source is covered by brand-literal guards. */
export const RENTER_PROMISE_FLAG = DRIVESHARE_PROMISE_FLAG;

export const FEATURE_FLAG_KEYS = [
  // ── Cross-brand redirects (US → driveshare.com) ──
  "feature_us_cross_brand_redirect",
  "feature_us_redirect_car_detail",
  "feature_us_hide_from_search",
  "feature_us_search_signpost",
  // Task #4401: when ON, blocks NEW US car listings on BookAClassic and steers
  // US hosts to DRIVESHARE's list-a-car flow. Independent of the search/booking
  // redirects above so listing can be blocked on its own. Default OFF.
  "feature_us_block_listing",

  // ── Referrals & growth ──
  "feature_referrals",
  "feature_show_app_store_badges",

  // ── Localisation rollout ──
  "feature_multi_language",

  // ── Listing & dashboard surfaces ──
  "feature_new_dashboard",
  "feature_pro_owner",
  "feature_market_data",
  "feature_suggested_rates",
  "feature_apex_score",

  // ── Verification surfaces ──
  // Gates the in-account Driver's licence verification row in the
  // personal-information panel (Settings → Personal). Hidden by default;
  // flip ON in the feature-flags admin UI to re-expose self-serve licence
  // verification.
  "feature_licence_verification",

  // Gates the rest-of-world (non-US / Didit) self-drive licence
  // verification booking hold. When OFF (default) a non-US self-drive
  // booking is NOT parked in `verification_hold` for licence verification —
  // host approval and instant bookings flow straight through to the normal
  // approved/confirmed + capture path, matching the booking-confirmation UI
  // which never prompts non-US renters to verify their licence. Flip ON in
  // the feature-flags admin UI to restore the previous non-US Didit hold
  // (hold until drivers.license_verification_status >= 1) with no code
  // deploy. The US Samba MVR hold is NEVER gated by this flag — US self-drive
  // bookings always hold until drivers.mvr_status === "approved".
  "feature_row_licence_verification_hold",

  // ── Tax ──
  // Task #1495: gate US Stripe Tax (sales tax) on bookings. When enabled and
  // the car's country is US, the quote includes an estimated sales-tax line
  // and PaymentIntents are created with automatic_tax enabled so Stripe
  // computes and collects tax at confirmation. Owner payouts stay pre-tax.
  "feature_us_stripe_tax",

  // ── Maintenance ──
  // Task #4299: when ON, the web SPA shows a full-screen "we're upgrading to
  // a brand new site" holding page in place of every route, EXCEPT /admin
  // (so an admin can turn it back off) and /maintenance itself. The guard is
  // client-side inside the SPA, so it always runs AFTER the site password
  // gate (siteGate). Default OFF — turning it on hides the entire site.
  // (Surfaces in the admin flags UI as "Maintenance Mode".)
  "feature_maintenance_mode",

  // ── Social sign-in buttons ──
  // Legacy combined gate retained for rollback/backward compatibility only.
  "feature_apple_google_signin",
  // Independent provider controls. Defaults stay fail-closed; production
  // migration 3263 enables Google while keeping Apple hidden.
  "feature_google_signin",
  "feature_apple_signin",

  // ── Instant Book ──
  // Task #4594: global master kill-switch for the renter-facing Instant Book
  // product. Default OFF — when off, Instant Book is suppressed everywhere
  // (booking always falls back to request-to-book, no instant badges/CTA, the
  // listing-wizard Instant Book section is hidden) WITHOUT touching hosts'
  // stored instant settings (instant_rental / instant_book_mode / instant
  // dates), so flipping it back ON restores each car exactly as configured.
  // The build-a-trip host quote (activeQuote) forced-instant path is exempt.
  "feature_instant_book",

  // ── Cross-brand login nudge (DriveShare → driveshare.com) ──
  // Task #4640: when ON, a DriveShare-origin user who signs into BookAClassic
  // sees a dismissible post-login modal nudging them to driveshare.com to
  // manage their US-based cars / make US bookings. Genuine BAC users never see
  // it; login is never blocked; the modal shows once per sign-in session.
  // Origin is determined server-side from the user record (source_id /
  // platform_origin), NOT the email domain. Default OFF.
  "feature_driveshare_login_redirect",

  // ── Renter dashboard surfaces ──
  // Task #4689: hide-switch for the "Journeys app" download promo banner on
  // the renter Trips page. Default OFF — banner shows exactly as today
  // (including its localStorage dismiss behavior). Flip ON in the
  // feature-flags admin UI to hide the banner everywhere it currently
  // appears on the renter dashboard, without a code deploy. ON = hide so the
  // fail-closed default (false) preserves the current "banner visible"
  // behavior.
  "feature_hide_journeys_promo",

  // ── DriveShare outbound email/SMS freeze ──
  // When ON, automated non-auth email and SMS to DriveShare-migrated
  // users/entities are suppressed. DriveShare admin members remain exempt
  // so operational comms still reach them. Independent of brand cutover.
  "feature_driveshare_email_freeze",

  // ── DriveShare Promise renter protection ──
  // Gates the renter-facing Promise widget, detail page, and mobile surface.
  // Default OFF so no Promise claim is exposed until explicitly enabled.
  DRIVESHARE_PROMISE_FLAG,

  // ── Host payout manual approval gate ──
  // When ON, EVERY Stripe host payout is held for an explicit admin go/no-go
  // sign-off before any money leaves the platform. Crons still run but instead
  // of paying they queue an approve/hold action in the admin Payouts →
  // Approvals tab; Approve fires the real payout, Hold moves no money. Gates
  // all three host-payout money chokepoints (Connect transfer, payout-link
  // settlement, drift top-up). Default OFF — when off, payouts fire
  // automatically exactly as today (byte-identical). The api-server resolves
  // this flag with money-safe semantics: an unreadable flag store HOLDS
  // payouts rather than letting them slip through unapproved.
  "feature_payout_manual_approval",

  // ── Win-back availability pre-check (owner probes) ──
  // Task #5598 (extension): when ON, unanswered request-to-book trips trigger
  // quiet "Is your car available?" probes to owners of similar nearby cars
  // after 6h. Probe responses are surfaced as availability badges on the
  // win-back alternatives email shown when the original booking dies.
  // Default OFF — all probe dispatch is suppressed, byte-identical to today.
  "feature_winback_precheck",

  // ── Search ranking ──
  // Task #6419: when ON, the search relevance score replaces the constant
  // response-rate placeholder with real per-car host-conversion signals
  // (request acceptance rate, response speed, expiry penalty) and adds a
  // strong ADDITIVE Instant Book boost (not a hard tier). Ranking-only —
  // no UI surface gates on it. Default OFF — ranking byte-identical to
  // today. Toggling `enabled` triggers an Algolia full re-index; the
  // nightly rebuild also picks it up.
  "feature_search_conversion_signals",

  // ── Post-payment phone verification (Task #6524) ──
  // When ON, the renter phone-verification gate moves AFTER payment:
  // /bookings/quote, POST /bookings and POST /checkout-sessions-pfc stop
  // requiring a verified phone up-front, and the renter is instead prompted
  // to verify on the booking-confirmation page + trips surfaces (with a
  // ~24h email/in-app reminder). Hosts see a "phone not yet verified"
  // indicator on affected bookings. Default OFF — checkout behavior is
  // byte-identical to today.
  "feature_phone_verify_post_payment",

  // ── Nearby car-events widget ──
  // When ON, hosts see a "Car events near you" section on the web
  // dashboard and in the mobile My Garage area, fed by the partner
  // events API via GET /api/host/events-nearby. Default OFF — no widget
  // anywhere until an admin enables it (dev DB row is toggled ON).
  "feature_car_events_widget",

  // ── Brand cutover (Task #8034) ──
  // When ON, the ENTIRE web app + API-served surfaces (emails, PDFs,
  // SEO/meta, auth screens, admin/Slack copy) switch from BookAClassic to
  // DRIVESHARE branding at runtime — no rebuild. The flag overrides the
  // build-time VITE_PLATFORM default (flag > env > "bac"). Native mobile
  // stays build-time (EXPO_PUBLIC_PLATFORM) by design: store binaries
  // cannot rebrand at runtime. Default OFF — BookAClassic branding is
  // byte-identical to today until an admin flips the flag.
  "brand_cutover_driveshare",

  // ── DSv2 claims kill switch (Task #20024) ──
  // The SUBORDINATE half of the insurance/claims gate:
  //     live ⇔ brand_cutover_driveshare AND feature_insurance_claims_v2
  // This one is a fail-OPEN kill switch (see api-server
  // lib/claim-eligibility.ts): a missing row / flag-store outage keeps it
  // ON so a post-cutover flag hiccup can't dark the live claims product.
  // The pre-cutover dark guarantee comes ENTIRELY from the brand master
  // switch above (default OFF) — never from this flag. Registered here so
  // the admin Feature Flags UI can see and kill it.
  "feature_insurance_claims_v2",
  // Host confirmation voice calls. Default OFF; server-side rollout controls
  // and the dialler kill switch are also required before any call is placed.
  HOST_CONFIRMATION_CALLS_FLAG,
] as const;

export type FeatureFlagKey = typeof FEATURE_FLAG_KEYS[number];

const FLAG_KEY_SET: ReadonlySet<string> = new Set(FEATURE_FLAG_KEYS);

export const FEATURE_FLAG_DISPLAY_NAMES: Readonly<Partial<Record<FeatureFlagKey, string>>> = {
  [HOST_CONFIRMATION_CALLS_FLAG]: "Host confirmation calls",
  [DRIVESHARE_PROMISE_FLAG]: "DriveShare Promise",
};

/** Type guard: is this string a known flag key? */
export function isFeatureFlagKey(value: unknown): value is FeatureFlagKey {
  return typeof value === "string" && FLAG_KEY_SET.has(value);
}

/**
 * Default values applied when the api-server hasn't returned a value for
 * a known flag (e.g. anonymous user, network timeout). These should be
 * conservative — `false` for anything that gates risky surfaces.
 */
export const FEATURE_FLAG_DEFAULTS: Readonly<Record<FeatureFlagKey, boolean>> = {
  feature_us_cross_brand_redirect: false,
  feature_us_redirect_car_detail: false,
  feature_us_hide_from_search: false,
  feature_us_search_signpost: false,
  feature_us_block_listing: false,
  feature_referrals: false,
  feature_show_app_store_badges: false,
  feature_multi_language: false,
  feature_new_dashboard: false,
  feature_pro_owner: false,
  feature_market_data: false,
  feature_suggested_rates: false,
  feature_apex_score: false,
  feature_licence_verification: false,
  // Default OFF — non-US (Didit) self-drive bookings are NOT held for licence
  // verification until an admin turns this on (US Samba hold is unaffected).
  feature_row_licence_verification_hold: false,
  feature_us_stripe_tax: false,
  feature_maintenance_mode: false,
  feature_apple_google_signin: false,
  feature_google_signin: false,
  feature_apple_signin: false,
  // Conversion-first ranking: Instant Book is DEFAULT-ON (kill switch only).
  feature_instant_book: true,
  feature_driveshare_login_redirect: false,
  feature_hide_journeys_promo: false,
  feature_driveshare_email_freeze: false,
  [DRIVESHARE_PROMISE_FLAG]: false,
  // Default OFF — payouts fire automatically exactly as today until an admin
  // turns the manual-approval gate on.
  feature_payout_manual_approval: false,
  // Default OFF — all probe dispatch is suppressed until explicitly enabled.
  feature_winback_precheck: false,
  // Default OFF — search ranking stays byte-identical to today until an
  // admin turns the conversion-signal scoring on.
  // Conversion-first ranking: DEFAULT-ON (kill switch only).
  feature_search_conversion_signals: true,
  // Default OFF — phone verification stays a pre-payment checkout gate until
  // an admin explicitly moves it post-payment.
  feature_phone_verify_post_payment: false,
  // Default OFF — the nearby car-events widget stays hidden on web +
  // mobile until an admin enables the flag.
  feature_car_events_widget: false,
  // Default OFF — the site stays on BookAClassic branding until an admin
  // explicitly flips the brand cutover.
  brand_cutover_driveshare: false,
  // Default ON — this is a fail-open KILL SWITCH, mirroring the server's
  // claimsFeatureEnabled(). Claims surfaces still stay dark until
  // brand_cutover_driveshare (default OFF) is flipped; this flag exists to
  // kill claims after cutover, not to launch them.
  feature_insurance_claims_v2: true,
  feature_host_confirmation_calls: false,
};

/**
 * Resolve a flag value from the api-server's response, falling back to the
 * declared default. Both apps should call this rather than reading the
 * flag map directly.
 */
export function resolveFlag(
  flags: Readonly<Record<string, boolean>> | null | undefined,
  key: FeatureFlagKey,
): boolean {
  if (flags && Object.prototype.hasOwnProperty.call(flags, key)) {
    return !!flags[key];
  }
  return FEATURE_FLAG_DEFAULTS[key];
}

